// Story 1.17, REPOINTED BY STORY 8.5 — TanStack Query mutation for
// POST /api/v1/creators/me/payout/account.
//
// Registers an approved creator's IBAN + holder name as the destination
// Greenhold settles their revenue share to. Approved creators only.
//
// The response carries the verification state: the sandbox provider verifies
// immediately ("verified"); a signed provider may answer
// "pending_verification" until its own IBAN check completes.
//
// The IBAN is never logged and never cached beyond this response — only
// iban_last4 comes back from the server.
//
// Deliberately NOT idempotency-keyed: the server side is an upsert keyed on
// user_id whose repeat is already a no-op, so a required header would buy
// nothing. The REQUEST endpoint (useRequestPayout) is keyed, because that one
// moves money.

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";
import {
  PAYMENT_ACCOUNT_QUERY_KEY,
  type PayoutAccount,
} from "./usePaymentAccountStatus";

export type RegisterPayoutAccountVariables = {
  iban: string;
  holder_name: string;
};

export type RegisterPayoutAccountError = {
  code: string;
  message: string;
  field?: string;
};

async function postPayoutAccount(
  variables: RegisterPayoutAccountVariables,
): Promise<PayoutAccount> {
  const response = await fetch("/api/v1/creators/me/payout/account", {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(variables),
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    let detail: RegisterPayoutAccountError = {
      code: "PAYOUT_ACCOUNT_FAILED",
      message: `Could not save your payout details (HTTP ${response.status})`,
    };
    try {
      const body = await response.json();
      if (body?.error) detail = body.error;
    } catch {
      // Body not JSON — keep the default error shape.
    }
    throw detail;
  }
  return (await response.json()) as PayoutAccount;
}

export function useRegisterPayoutAccount() {
  const queryClient = useQueryClient();
  return useMutation<
    PayoutAccount,
    RegisterPayoutAccountError,
    RegisterPayoutAccountVariables
  >({
    mutationFn: postPayoutAccount,
    onSuccess: (account) => {
      // Seed the status query with the fresh state, then invalidate so
      // any in-flight consumers reconcile with the server.
      queryClient.setQueryData(PAYMENT_ACCOUNT_QUERY_KEY, account);
      queryClient.invalidateQueries({ queryKey: PAYMENT_ACCOUNT_QUERY_KEY });
    },
  });
}
